/**
 * The module contract. A module is one domain (breeding, health, sales...) described by pure
 * data and pure functions so the server, the web app and the agent all read the same truth.
 * Server-only behavior lives in modules/<id>/server.ts; UI lives in modules/<id>/web.tsx.
 */
import type { Fields, Issue, RefTarget } from "./fields";
import type { ObservedTime, Precision } from "./time";
import type { Permission } from "./roles";

export type SubjectKind = "animal" | "group" | "place" | "contact" | "stock_item" | "stock_lot" | "asset" | "job" | "fin_doc";

/** Color families for categories. Status always also has words and an icon. */
export type Tone =
  | "herd" | "repro" | "health" | "perf" | "genetics" | "land" | "feed" | "money" | "ops" | "sale" | "dairy" | "note"
  | "good" | "warn" | "bad" | "info" | "neutral";

/** A translatable message: English catalog key with {{placeholders}}. */
export interface Msg { text: string; values?: Record<string, string | number> }
export const msg = (text: string, values?: Msg["values"]): Msg => ({ text, values });

export type Translate = (text: string, values?: Record<string, unknown>) => string;

export interface Formatters {
  t: Translate;
  date(value: string | null | undefined, precision?: Precision): string;
  quantity(q: { value: number; unit: string } | null | undefined): string;
  money(m: { minor: number; currency: string } | null | undefined): string;
  /** Display label for a referenced id (tag, name...). */
  label(id: string | null | undefined): string;
  list(items: string[]): string;
  count(n: number, one: string, other: string): string;
}

export interface SubjectSpec {
  role: string;               // "animal", "dam", "calf", "bull", "from", "to"
  kind: SubjectKind;
  label: string;
  many?: boolean;
  required?: boolean;
  help?: string;
}

export interface SubjectRef { kind: SubjectKind; id: string; role: string; label?: string }

export interface StockEffect {
  /** Either a lot or an item; the lot implies its item. */
  lot_id?: string;
  item_id?: string;
  delta: number;              // negative for use
  unit: string;
  reason: "use" | "receive" | "adjust" | "transfer" | "sell" | "waste" | "count";
}

export interface FollowUp {
  key: string;                // stable per event so corrections update rather than duplicate
  title: Msg;
  due_at: string;             // YYYY-MM-DD
  subject?: { kind: SubjectKind; id: string };
}

export interface EventLike<D = Record<string, unknown>> {
  type: string;
  data: D;
  subjects: SubjectRef[];
  occurred_at?: string | null;
  occurred_precision?: Precision;
}

export interface EventTypeDef<D = any> {
  /** `<module>.<name>`, stable forever; stored in every event row. */
  type: string;
  label: string;
  description: string;
  icon: string;               // icon name from web/ui/icons.ts or the module's icon map
  tone: Tone;
  subjects: SubjectSpec[];
  fields: Fields;
  /** Plain-language one-liner, localized by the formatter: "Calved: heifer, 42P, no help". */
  summary(e: EventLike<D>, f: Formatters): string;
  /** always: never filed without a person looking (sales, deaths, money, stock, new animals). */
  review?: "auto" | "always";
  /** Who may record it. Defaults to "record". */
  permission?: Permission;
  agent?: {
    when: string;             // when to choose this type
    avoid?: string;           // common confusions
    examples?: string[];      // short spoken examples
  };
  effects?: {
    stock?(data: D, subjects: SubjectRef[]): StockEffect[];
    followUps?(data: D, occurredOn: string | null, subjects: SubjectRef[]): FollowUp[];
  };
  /** Pure checks beyond the field schema. Cross-record checks belong to the server module. */
  validate?(data: D, subjects: SubjectRef[]): Issue[];
  /** Hide from "Add a record" menus (system or import-only types). */
  hidden?: boolean;
}

export interface FacetEvent<D = any> {
  id: string;
  /** First revision's id, stable across corrections. Use it to link one event to another. */
  root_id?: string;
  type: string;
  occurred_at: string | null;
  occurred_precision: Precision;
  occurred_end: string | null;
  recorded_at: string;
  data: D;
  /** Roles this subject plays in the event, e.g. ["dam"]. */
  roles: string[];
  subjects: SubjectRef[];
  job_id: string | null;
  source_kind: string;
}

export interface Badge { label: Msg; tone: Tone; icon?: string }
export interface Fact { label: string; value: Msg | string; tone?: Tone; icon?: string }

/** Core animal columns a facet may project (current location, lifecycle status). */
export interface AnimalProjection {
  location_id?: string | null;
  location_since?: string | null;
  status?: string;
  lot_id?: string | null;
}

export interface FacetDef<S = any> {
  id: string;                 // usually the module id; unique across modules
  subject: SubjectKind;
  eventTypes: string[];
  /** Bump when reduce changes so stored facets recompute. */
  version: number;
  /** `settings` is the ranch's settings object (module settings live under the module id). */
  reduce(events: FacetEvent[], ctx: { today: string; settings?: Record<string, unknown> }): S | null;
  project?(state: S | null): AnimalProjection;
  badges?(state: S, ctx: { today: string }): Badge[];
  facts?(state: S, ctx: { today: string }): Fact[];
  /** Filterable fields for lists and the agent's query tool: name -> description. */
  filters?: Record<string, { label: string; type: "enum" | "date" | "number" | "boolean" | "text"; options?: Record<string, string> }>;
}

export interface JobTypeDef {
  type: string;               // `<module>.<name>`
  label: string;
  description: string;
  icon: string;
  tone: Tone;
  workspace: string;          // workspace id that owns its UI
  subjectKind?: SubjectKind;  // what the worklist holds
  /** The event recorded for each worklist item, when the job is a round of the same work. */
  itemEvent?: string;
  fields?: Fields;            // job settings (season dates, association, sale date...)
  stages?: Array<{ id: string; label: string }>;
  agent?: { when: string };
}

export type WorkspaceGroup = "breed" | "care" | "operations" | "sell" | "data";

export interface WorkspaceDef {
  id: string;                 // route segment under /work
  label: string;
  description: string;
  icon: string;
  group: WorkspaceGroup;
  /** Search terms in English; Spanish terms come from the catalog plus `keywordsEs`. */
  keywords: string[];
  keywordsEs?: string[];
  /** Presets that pin it by default. */
  presets?: Array<"basic" | "seedstock" | "commercial" | "feedlot" | "dairy">;
  permission?: Permission;
}

export interface RanchSectionDef {
  id: string;                 // route segment under /ranch
  label: string;
  description: string;
  icon: string;
  group: "places" | "supplies" | "equipment" | "people" | "money" | "reports" | "data";
  keywords: string[];
  keywordsEs?: string[];
  permission?: Permission;
}

export interface StockKindDef {
  kind: string;               // must be one of the stock_items.kind values
  label: string;
  fields: Fields;             // stored in stock_items.attrs
  lotFields?: Fields;         // stored in stock_lots.attrs
}

export interface ModuleContract {
  id: string;
  label: string;
  description: string;
  events?: EventTypeDef[];
  facets?: FacetDef[];
  jobs?: JobTypeDef[];
  workspaces?: WorkspaceDef[];
  ranchSections?: RanchSectionDef[];
  stockKinds?: StockKindDef[];
  /** Extra animal/place/contact attributes stored in attrs under `<module>.<key>`. */
  attributes?: Partial<Record<"animal" | "place" | "contact" | "asset" | "group", Fields>>;
  /** Ranch-level settings stored in ranches.settings under the module id. */
  settings?: Fields;
  /** Extra catalog strings that are built dynamically (badge templates, report column names...). */
  messages?: string[];
}

export function defineModule<M extends ModuleContract>(module: M): M {
  return module;
}

export function defineEvent<D = Record<string, unknown>>(def: EventTypeDef<D>): EventTypeDef<D> {
  return def;
}

export function defineFacet<S>(def: FacetDef<S>): FacetDef<S> {
  return def;
}

export type { Fields, Issue, ObservedTime, Precision, RefTarget };
